import { type Query } from '@inpart-io/devtools-iap';
import { computed, type ComputedRef, onMounted, type Ref, ref, watch } from 'vue';
import { useStore } from 'vuex';

import { COUNT_BY_QUERY, INCREMENT_COUNT } from '@/store/base/search/actions';
import { GET_ROWS_COUNT } from '@/store/base/search/getters';

export const TOTAL_COUNT_KEY = 'total-count';

export function useCountHelper(
    module: string,
    query: Query,
    customKey?: string
): {
    totalCount: ComputedRef<number>;
    countQuery: Ref<Query>;
    countLoading: Ref<boolean>;
    count: () => Promise<void>;
    updateCount: (inc?: number) => Promise<void>;
} {
    const store = useStore();
    const countQuery = ref(query);
    const key = customKey ? `${customKey}-${TOTAL_COUNT_KEY}` : TOTAL_COUNT_KEY;
    const countLoading = ref(false);

    const count = async (): Promise<void> => {
        countLoading.value = true;
        return store.dispatch(`${module}/${COUNT_BY_QUERY}`, { key, query: countQuery.value }).then(() => {
            countLoading.value = false;
        });
    };

    onMounted(() => {
        void count();
    });

    const totalCount = computed((): number => {
        return store.getters[`${module}/${GET_ROWS_COUNT}`](key);
    });

    const updateCount = async (inc?: number): Promise<void> => {
        await store.dispatch(`${module}/${INCREMENT_COUNT}`, { key, increment: inc });
    };

    watch(countQuery, (newValue: Query) => {
        countLoading.value = true;
        store
            .dispatch(`${module}/${COUNT_BY_QUERY}`, { key, query: newValue })
            .then(() => {
                countLoading.value = false;
            })
            .catch((error) => {
                console.error(error);
            });
    });

    return { count, totalCount, updateCount, countQuery, countLoading };
}
